// ═══════════════════════════════════════════════════════════════
// PersonnelManagerView.tsx – Személyzet & szervezeti hierarchia
//   1. Szervezet: egységek → pozíciók → munkatársak egy nézetben (blokk / lenyíló / táblázat)
//   2. Szervezeti diagram: álló / fekvő, részletesség, húzással átrendezhető, „alá / mellé” elhelyezés
//   3. Névsor & elérhetőségek: munkatársak kártyán / listában, kapcsolattartói kártyák
// A diagram és a kapcsolattartók élő blokkként beszúrhatók a bejegyzésekbe (Változó → Szervezet & személyzet).
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ApiClient } from '../../api/client';
import type { OrgUnit, Position, Person, OrgChartSettings, LinkedEntity } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { PhoneInput } from '../common/PhoneInput';
import {
  Users,
  Building,
  Briefcase,
  Search,
  Mail,
  Phone,
  MapPin,
  RefreshCw,
  UserCheck,
  LayoutGrid,
  List as ListIcon,
  GitFork,
  Edit2,
  Trash2,
  PhoneCall,
  Building2,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Table2,
  ChevronsDownUp,
  Network,
  Save,
  Copy,
  Check,
  AlertTriangle,
  Info,
  RectangleVertical,
  RectangleHorizontal,
  FolderPlus,
  UserPlus,
} from 'lucide-react';
import { naturalCompare } from '../../utils/sort';
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';
import { OrgStructurePanel, type OrgViewMode } from './OrgStructurePanel';
import { OrgChart } from './OrgChart';
import { fromOverview, childUnits, descendantIds, ORG_CHART_DEFAULTS, type OrgModel } from './orgModel';

type MainTab = 'structure' | 'chart' | 'directory';
type DirMode = 'cards' | 'table' | 'contacts';

const inputCls = 'w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
const labelCls = 'block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1';

/** az egységek fa-sorrendben, mélységgel (választólistákhoz) */
function unitTreeOrder(m: OrgModel): { u: OrgUnit; depth: number }[] {
  const out: { u: OrgUnit; depth: number }[] = [];
  const walk = (pid: number | null, depth: number) => {
    if (depth > 15) return;
    for (const u of childUnits(m, pid)) { out.push({ u, depth }); walk(u.id, depth + 1); }
  };
  walk(null, 0);
  return out;
}

const TabBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 sm:px-4 py-3 text-sm font-semibold border-b-2 transition flex items-center gap-2 cursor-pointer ${
      active
        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
    }`}
  >
    {children}
  </button>
);

const SegBtn: React.FC<{ active: boolean; onClick: () => void; title: string; children: React.ReactNode }> = ({ active, onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-pressed={active}
    className={`px-2 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition cursor-pointer ${
      active ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
    }`}
  >
    {children}
  </button>
);

export const PersonnelManagerView: React.FC = () => {
  const { role, tenant } = useAuth();
  const [activeTab, setActiveTab] = usePersistedViewMode<MainTab>('personnel_tab', 'structure', ['structure', 'chart', 'directory']);
  // a korábbi „Kártyák / Lista” választás megmarad (ugyanaz a kulcs), új: kapcsolattartói kártyák
  const [dirMode, setDirMode] = usePersistedViewMode<DirMode>('personnel', 'cards', ['cards', 'table', 'contacts']);
  const [structMode, setStructMode] = usePersistedViewMode<OrgViewMode>('personnel_structure', 'accordion', ['blocks', 'accordion', 'table']);
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [assignments, setAssignments] = useState<{ person_id: number; position_id: number }[]>([]);
  const [hierarchyReady, setHierarchyReady] = useState(true);
  const [chartSettings, setChartSettings] = useState<OrgChartSettings>(ORG_CHART_DEFAULTS);
  const [chartDraft, setChartDraft] = useState<OrgChartSettings>(ORG_CHART_DEFAULTS);
  const [chartSaving, setChartSaving] = useState(false);
  const [chartMsg, setChartMsg] = useState<string | null>(null);
  const [chartBusy, setChartBusy] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<{ id: number; name: string; slug: string }[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(tenant?.id);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modals & Editing
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Partial<Person> | null>(null);

  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Partial<OrgUnit> | null>(null);

  const [posModalOpen, setPosModalOpen] = useState(false);
  const [editingPos, setEditingPos] = useState<Partial<Position> | null>(null);

  const canEdit = ['superadmin', 'tenant_admin', 'org_admin', 'editor'].includes(role);
  // v61: melyik szerv belső szervezete (null = az anyaszervezet; egyébként egy kapcsolt intézmény)
  const [entityId, setEntityId] = useState<number | null>(null);
  const [linkedEntities, setLinkedEntities] = useState<LinkedEntity[]>([]);
  const [entityReady, setEntityReady] = useState(true);
  const tenantPayload = () => ({ ...(selectedTenantId ? { tenant_id: Number(selectedTenantId) } : {}), ...(entityId ? { entity_id: entityId } : {}) });

  const loadEntities = async (tid?: number) => {
    const r = await ApiClient.getLinkedEntities({ tenant_id: tid ?? selectedTenantId });
    setLinkedEntities(r.ok && r.data ? [...r.data].sort((a, b) => a.name.localeCompare(b.name, 'hu')) : []);
  };

  const loadData = async (tid?: number, quiet = false, eid?: number | null) => {
    if (!quiet) setLoading(true);
    const targetTid = tid !== undefined ? tid : selectedTenantId;
    const targetEid = eid !== undefined ? eid : entityId;
    const res = await ApiClient.getPersonnelOverview(targetTid, targetEid);
    if (!res.ok && targetEid) {
      // a kapcsolt intézmény már nem elérhető (pl. törölték) → vissza az anyaszervezetre
      setEntityId(null);
      if (!quiet) setLoading(false);
      return loadData(targetTid, quiet, null);
    }
    if (res.ok && res.data) {
      setOrgUnits(res.data.org_units || []);
      setPositions(res.data.positions || []);
      setPersons(res.data.persons || []);
      setAssignments(res.data.current_assignments || []);
      setHierarchyReady(res.data.hierarchy_ready !== false);
      setEntityReady(res.data.entity_ready !== false);
      const cs = { ...ORG_CHART_DEFAULTS, ...(res.data.chart_settings || {}) };
      setChartSettings(cs);
      if (!quiet) setChartDraft(cs);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (['superadmin', 'tenant_admin'].includes(role)) {
      ApiClient.getTenants().then(res => {
        if (res.ok && res.data && res.data.length > 0) {
          setTenants(res.data);
          const nonSystem = res.data.find(t => t.id !== 1);
          if (nonSystem && (!tenant?.id || tenant.id === 1)) {
            setSelectedTenantId(nonSystem.id);
            setEntityId(null);
            loadData(nonSystem.id, false, null);
            loadEntities(nonSystem.id);
            return;
          }
        }
        loadData(undefined, false, null);
        loadEntities();
      });
    } else {
      loadData(undefined, false, null);
      loadEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const model = useMemo(() => fromOverview(orgUnits, positions, persons, assignments), [orgUnits, positions, persons, assignments]);
  const treeOrder = useMemo(() => unitTreeOrder(model), [model]);
  const selectedEntity = entityId ? linkedEntities.find((e) => e.id === entityId) : null;
  // a diagram gyökérfelirata: a kiválasztott szerv neve
  const tenantName = selectedEntity?.name || tenants.find(t => t.id === selectedTenantId)?.name || tenant?.name || '';
  const tokenScope = entityId ? `intezmeny-${entityId}` : '';
  const scoped = (kind: string, unit?: number) => `{{${kind}${tokenScope || unit ? ':' : ''}${tokenScope}${tokenScope && unit ? ':' : ''}${unit ?? ''}}}`;

  // ── PERSON CRUD ──────────────────────────────────────────────
  const handleOpenNewPerson = (unitId: number | null = null, positionId: number | null = null) => {
    setEditingPerson({
      name: '',
      title: '',
      email: '',
      phone: '',
      room: '',
      org_unit_id: unitId,
      position_id: positionId,
      active: 1,
    });
    setPersonModalOpen(true);
  };

  const handleOpenEditPerson = (p: Person) => {
    setEditingPerson({
      ...p,
      org_unit_id: p.org_unit_id ? Number(p.org_unit_id) : null,
      position_id: p.position_id ? Number(p.position_id) : null,
      title: p.title || '',
      room: p.room || '',
      email: p.email || '',
      phone: p.phone || '',
    });
    setPersonModalOpen(true);
  };

  const handleSavePerson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPerson || !editingPerson.name) return;

    const payload = {
      name: editingPerson.name.trim(),
      title: editingPerson.title ? editingPerson.title.trim() : null,
      email: editingPerson.email || '',
      phone: editingPerson.phone || '',
      room: editingPerson.room ? editingPerson.room.trim() : null,
      org_unit_id: editingPerson.org_unit_id ? Number(editingPerson.org_unit_id) : null,
      position_id: editingPerson.position_id ? Number(editingPerson.position_id) : null,
      active: editingPerson.active !== undefined ? editingPerson.active : 1,
      tenant_id: selectedTenantId ? Number(selectedTenantId) : undefined,
      ...(entityId ? { entity_id: entityId } : {}),
    };

    if (editingPerson.id) {
      const res = await ApiClient.updatePerson(editingPerson.id, payload);
      if (res.ok) {
        setPersonModalOpen(false);
        await loadData(undefined, true);
      } else {
        alert(res.error || 'Hiba a munkatárs frissítésekor');
      }
    } else {
      const res = await ApiClient.createPerson(payload);
      if (res.ok) {
        setPersonModalOpen(false);
        await loadData(undefined, true);
      } else {
        alert(res.error || 'Hiba a munkatárs létrehozásakor');
      }
    }
  };

  const handleDeletePerson = async (id: number, name: string) => {
    if (!confirm(`Biztosan törölni / inaktiválni szeretné "${name}" munkatársat?`)) return;
    const res = await ApiClient.deletePerson(id);
    if (res.ok) {
      loadData(undefined, true);
    } else {
      alert(res.error || 'Hiba a törlés során');
    }
  };

  // ── ORG UNIT CRUD ────────────────────────────────────────────
  const nextSortOrder = (parentId: number | null, placement: 'below' | 'beside' = 'below') =>
    childUnits(model, parentId, placement).reduce((mx, u) => Math.max(mx, u.sort_order || 0), 0) + 1;

  const handleOpenNewUnit = (parentId: number | null = null) => {
    setEditingUnit({ name: '', code: '', email: '', phone: '', address: '', description: '', parent_id: parentId, placement: 'below', sort_order: nextSortOrder(parentId) });
    setUnitModalOpen(true);
  };

  const handleOpenEditUnit = (u: OrgUnit) => {
    setEditingUnit({ ...u, parent_id: u.parent_id ?? null, placement: u.placement === 'beside' ? 'beside' : 'below' });
    setUnitModalOpen(true);
  };

  const handleSaveUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUnit || !editingUnit.name) return;
    const { leader_name: _ln, leader_title: _lt, ...rest } = editingUnit as any;
    const payload: Partial<OrgUnit> & { tenant_id?: number } = {
      ...rest,
      parent_id: editingUnit.parent_id ? Number(editingUnit.parent_id) : null,
      sort_order: Number(editingUnit.sort_order) || 0,
      ...tenantPayload(),
    };
    if (!hierarchyReady) {
      // a séma frissítéséig csak a név és a sorrend menthető
      const minimal: any = { name: payload.name, sort_order: payload.sort_order, ...tenantPayload() };
      Object.keys(payload).forEach((k) => { if (!(k in minimal)) delete (payload as any)[k]; });
      Object.assign(payload, minimal);
    }
    const res = editingUnit.id ? await ApiClient.updateOrgUnit(editingUnit.id, payload) : await ApiClient.createOrgUnit(payload);
    if (res.ok) {
      setUnitModalOpen(false);
      loadData(undefined, true);
    } else alert(res.error || 'Hiba a mentéskor');
  };

  const handleDeleteUnit = async (id: number, name: string) => {
    const kids = childUnits(model, id).length;
    const msg = `Biztosan törölni szeretné a(z) "${name}" szervezeti egységet?` +
      (kids ? `\n\nAz alegységei (${kids}) a fölérendelt egység alá kerülnek.` : '') +
      '\nA pozíciói és munkatársai megmaradnak, „egységhez nem rendelt” állapotba kerülnek.';
    if (!confirm(msg)) return;
    const res = await ApiClient.deleteOrgUnit(id);
    if (res.ok) loadData(undefined, true);
    else alert(res.error || 'Hiba a törléskor');
  };

  // ── POSITION CRUD ────────────────────────────────────────────
  const handleOpenNewPos = (unitId: number | null = null) => {
    const uid = unitId ?? null;
    const siblings = positions.filter((p) => (p.org_unit_id ?? null) === uid);
    setEditingPos({ title: '', org_unit_id: uid, is_leader: 0, multi_person: 0, sort_order: siblings.reduce((mx, p) => Math.max(mx, p.sort_order || 0), 0) + 1, job_description: '' });
    setPosModalOpen(true);
  };

  const handleOpenEditPos = (pos: Position) => {
    setEditingPos({ ...pos });
    setPosModalOpen(true);
  };

  const handleSavePos = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPos || !editingPos.title) return;
    const payload: any = {
      title: editingPos.title.trim(),
      org_unit_id: editingPos.org_unit_id ? Number(editingPos.org_unit_id) : null,
      sort_order: Number(editingPos.sort_order) || 0,
      multi_person: editingPos.multi_person ? 1 : 0,
      ...tenantPayload(),
    };
    if (editingPos.short_title !== undefined) payload.short_title = editingPos.short_title;
    if (hierarchyReady) {
      payload.is_leader = editingPos.is_leader ? 1 : 0;
      payload.job_description = editingPos.job_description || '';
    }
    const res = editingPos.id ? await ApiClient.updatePosition(editingPos.id, payload) : await ApiClient.createPosition(payload);
    if (res.ok) {
      setPosModalOpen(false);
      loadData(undefined, true);
    } else alert(res.error || 'Hiba a mentéskor');
  };

  const handleDeletePos = async (id: number, title: string) => {
    if (!confirm(`Biztosan törölni szeretné a(z) "${title}" pozíciót?`)) return;
    const res = await ApiClient.deletePosition(id);
    if (res.ok) loadData(undefined, true);
    else alert(res.error || 'Hiba a törléskor');
  };

  // ── DIAGRAM SZERKESZTÉS ──────────────────────────────────────
  const runChartOps = async (ops: { id: number; patch: Partial<OrgUnit> }[]) => {
    if (!ops.length) return;
    setChartBusy(true);
    try {
      for (const op of ops) {
        const res = await ApiClient.updateOrgUnit(op.id, op.patch);
        if (!res.ok) { alert(res.error || 'Hiba a módosításkor'); break; }
      }
    } finally {
      await loadData(undefined, true);
      setChartBusy(false);
    }
  };

  const parentOf = (u: OrgUnit) => (u.parent_id && model.units.some((x) => x.id === u.parent_id) ? u.parent_id : null);

  const chartHandlers = canEdit && hierarchyReady ? {
    onMove: (unitId: number, newParentId: number | null) => {
      const u = model.units.find((x) => x.id === unitId);
      if (!u || parentOf(u) === newParentId) return;
      runChartOps([{ id: unitId, patch: { parent_id: newParentId, sort_order: nextSortOrder(newParentId, u.placement || 'below') } }]);
    },
    onTogglePlacement: (u: OrgUnit) => {
      const next = u.placement === 'beside' ? 'below' : 'beside';
      runChartOps([{ id: u.id, patch: { placement: next, sort_order: nextSortOrder(parentOf(u), next) } }]);
    },
    onReorder: (u: OrgUnit, dir: -1 | 1) => {
      const sib = childUnits(model, parentOf(u), u.placement || 'below');
      const i = sib.findIndex((x) => x.id === u.id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= sib.length) return;
      const arr = [...sib];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      runChartOps(arr.map((x, k) => ({ id: x.id, patch: { sort_order: k + 1 }, cur: x.sort_order })).filter((o) => o.cur !== o.patch.sort_order).map(({ id, patch }) => ({ id, patch })));
    },
    onAddChild: (parentId: number | null) => handleOpenNewUnit(parentId),
    onEdit: (u: OrgUnit) => handleOpenEditUnit(u),
  } : undefined;

  const chartDirty = JSON.stringify(chartDraft) !== JSON.stringify(chartSettings);
  const saveChartSettings = async () => {
    setChartSaving(true); setChartMsg(null);
    const res = await ApiClient.saveOrgChartSettings(chartDraft, selectedTenantId, entityId);
    setChartSaving(false);
    if (res.ok) {
      const cs = { ...ORG_CHART_DEFAULTS, ...(res.data || chartDraft) };
      setChartSettings(cs); setChartDraft(cs);
      setChartMsg('Elmentve – a nyilvános oldalon is így jelenik meg.');
      setTimeout(() => setChartMsg(null), 3500);
    } else setChartMsg(res.error || 'Hiba a mentéskor');
  };

  const copyToken = useCallback(async (token: string) => {
    try { await navigator.clipboard.writeText(token); } catch { /* a vágólap nem elérhető */ }
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1800);
  }, []);

  // Filters
  const filteredPersons = persons.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.title && p.title.toLowerCase().includes(search.toLowerCase())) ||
    (p.email && p.email.toLowerCase().includes(search.toLowerCase())) ||
    (p.phone && p.phone.toLowerCase().includes(search.toLowerCase())) ||
    (p.room && p.room.toLowerCase().includes(search.toLowerCase())) ||
    (p.org_unit_name && p.org_unit_name.toLowerCase().includes(search.toLowerCase()))
  );

  const [personSortCol, setPersonSortCol] = useState<'name' | 'org_unit' | 'contact' | 'room' | 'status'>('name');
  const [personSortDir, setPersonSortDir] = useState<'asc' | 'desc'>('asc');

  const handlePersonSort = (col: 'name' | 'org_unit' | 'contact' | 'room' | 'status') => {
    if (personSortCol === col) {
      setPersonSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPersonSortCol(col);
      setPersonSortDir('asc');
    }
  };

  const sortedPersons = [...filteredPersons].sort((a, b) => {
    let valA = '';
    let valB = '';
    if (personSortCol === 'name') {
      valA = a.name;
      valB = b.name;
    } else if (personSortCol === 'org_unit') {
      valA = a.org_unit_name || '';
      valB = b.org_unit_name || '';
    } else if (personSortCol === 'contact') {
      valA = a.email || a.phone || '';
      valB = b.email || b.phone || '';
    } else if (personSortCol === 'room') {
      valA = a.room || '';
      valB = b.room || '';
    } else if (personSortCol === 'status') {
      valA = a.active ? '1' : '0';
      valB = b.active ? '1' : '0';
    }
    const cmp = naturalCompare(valA, valB);
    return personSortDir === 'asc' ? cmp : -cmp;
  });

  const findPerson = (id: number) => persons.find((p) => p.id === id);
  const findPosition = (id: number) => positions.find((p) => p.id === id);

  const unitOptions = (excludeIds?: Set<number>) => treeOrder
    .filter(({ u }) => !excludeIds || !excludeIds.has(u.id))
    .map(({ u, depth }) => <option key={u.id} value={u.id}>{`${'  '.repeat(depth)}${depth ? '└ ' : ''}${u.name}`}</option>);

  const primaryBtn = 'flex items-center gap-2 px-3.5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl text-sm shadow-md transition cursor-pointer';
  const secondaryBtn = 'flex items-center gap-2 px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium rounded-xl text-sm shadow-xs transition cursor-pointer';

  const tokenList: { token: string; label: string }[] = [
    { token: scoped('SZERVEZETI_DIAGRAM'), label: selectedEntity ? `${selectedEntity.short_name || selectedEntity.name} – teljes diagram` : 'Teljes szervezeti diagram' },
    { token: scoped('KAPCSOLATOK'), label: selectedEntity ? `${selectedEntity.short_name || selectedEntity.name} – kapcsolattartók` : 'Kapcsolattartók (minden egység)' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Users className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Személyzet & Szervezeti Hierarchia</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            2011. évi CXII. tv. I. rész: Szervezeti felépítés, vezetők, munkatársak, kapcsolattartás és szervezeti diagram
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {tenants.length > 1 && (
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-2.5 py-1.5 shadow-xs">
              <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span className="text-xs text-slate-500 font-medium">Szervezet:</span>
              <select
                value={selectedTenantId || ''}
                onChange={(e) => {
                  const tid = Number(e.target.value);
                  setSelectedTenantId(tid);
                  setEntityId(null);
                  loadData(tid, false, null);
                  loadEntities(tid);
                }}
                className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-200 focus:outline-none cursor-pointer"
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id} className="dark:bg-slate-900 text-slate-900 dark:text-slate-100">{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {linkedEntities.length > 0 && (
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-2.5 py-1.5 shadow-xs">
              <Network className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <label htmlFor="pm-entity" className="text-xs text-slate-500 font-medium">Szerv:</label>
              <select id="pm-entity"
                value={entityId ?? ''}
                disabled={!entityReady && !entityId}
                title={!entityReady ? 'A kapcsolt intézmények belső szervezete az adatbázis frissítése (v61) után kezelhető' : 'Melyik szerv belső szervezetét kezeled'}
                onChange={(e) => {
                  const eid = e.target.value ? Number(e.target.value) : null;
                  setEntityId(eid);
                  setSearch('');
                  loadData(undefined, false, eid);
                }}
                className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-200 focus:outline-none cursor-pointer max-w-[16rem] disabled:opacity-50"
              >
                <option value="">Saját szervezet</option>
                <optgroup label="Kapcsolt intézmények">
                  {linkedEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
                </optgroup>
              </select>
            </div>
          )}

          <button
            onClick={() => loadData()}
            className="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl transition cursor-pointer shadow-xs"
            title="Frissítés"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              {(activeTab === 'structure' || activeTab === 'chart') && (
                <button onClick={() => handleOpenNewUnit(null)} className={primaryBtn}>
                  <FolderPlus className="w-4 h-4" />
                  <span>Új egység</span>
                </button>
              )}
              {activeTab === 'structure' && (
                <button onClick={() => handleOpenNewPos(null)} className={secondaryBtn}>
                  <Briefcase className="w-4 h-4" />
                  <span>Új pozíció</span>
                </button>
              )}
              {(activeTab === 'structure' || activeTab === 'directory') && (
                <button onClick={() => handleOpenNewPerson()} className={activeTab === 'directory' ? primaryBtn : secondaryBtn}>
                  <UserPlus className="w-4 h-4" />
                  <span>Új munkatárs</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedEntity && (
        <div className="p-3 rounded-2xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 flex flex-wrap items-center justify-between gap-2 text-xs text-emerald-900 dark:text-emerald-200">
          <span className="flex items-center gap-2"><Network className="w-4 h-4 shrink-0" />
            <span><b>{selectedEntity.name}</b> belső szervezetét kezeled (kapcsolt intézmény). Az itt felvett egységek, pozíciók és munkatársak az intézményhez tartoznak, a saját szervezet diagramján nem jelennek meg.</span>
          </span>
          <button type="button" onClick={() => { setEntityId(null); setSearch(''); loadData(undefined, false, null); }}
            className="px-2.5 py-1 rounded-lg border border-emerald-300 dark:border-emerald-700 font-semibold hover:bg-white/60 dark:hover:bg-emerald-900/40 cursor-pointer">Vissza a saját szervezethez</button>
        </div>
      )}

      {!hierarchyReady && (
        <div className="p-3.5 rounded-2xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 flex items-start gap-2.5 text-xs text-amber-900 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>Adatbázis-frissítés szükséges.</b> Az egységek hierarchiája (alá / mellé), az egységek elérhetőségei és a vezetői pozíció
            csak a v60-as séma után menthető. Rendszergazda: <i>Adatbázis-séma &amp; Teljes mentés → Frissítés</i>. Addig a név és a sorrend módosítható.
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          <TabBtn active={activeTab === 'structure'} onClick={() => setActiveTab('structure')}>
            <Building className="w-4 h-4" />
            <span>Szervezet ({orgUnits.length} egység · {positions.length} pozíció)</span>
          </TabBtn>
          <TabBtn active={activeTab === 'chart'} onClick={() => setActiveTab('chart')}>
            <GitFork className="w-4 h-4 text-emerald-500" />
            <span>Szervezeti diagram</span>
          </TabBtn>
          <TabBtn active={activeTab === 'directory'} onClick={() => setActiveTab('directory')}>
            <UserCheck className="w-4 h-4" />
            <span>Névsor & elérhetőségek ({persons.length})</span>
          </TabBtn>
        </div>

        {activeTab === 'structure' && (
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Megjelenítés">
            <SegBtn active={structMode === 'blocks'} onClick={() => setStructMode('blocks')} title="Blokkos megjelenítés"><LayoutGrid className="w-3.5 h-3.5" /><span className="hidden sm:inline">Blokk</span></SegBtn>
            <SegBtn active={structMode === 'accordion'} onClick={() => setStructMode('accordion')} title="Lenyíló lista"><ChevronsDownUp className="w-3.5 h-3.5" /><span className="hidden sm:inline">Lenyíló</span></SegBtn>
            <SegBtn active={structMode === 'table'} onClick={() => setStructMode('table')} title="Táblázatos megjelenítés"><Table2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Táblázat</span></SegBtn>
          </div>
        )}

        {activeTab === 'directory' && (
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Megjelenítés">
            <SegBtn active={dirMode === 'cards'} onClick={() => setDirMode('cards')} title="Kártyás megjelenítés"><LayoutGrid className="w-3.5 h-3.5" /><span className="hidden sm:inline">Kártyák</span></SegBtn>
            <SegBtn active={dirMode === 'table'} onClick={() => setDirMode('table')} title="Táblázatos / lista megjelenítés"><ListIcon className="w-3.5 h-3.5" /><span className="hidden sm:inline">Lista</span></SegBtn>
            <SegBtn active={dirMode === 'contacts'} onClick={() => setDirMode('contacts')} title="Kapcsolattartói kártyák"><PhoneCall className="w-3.5 h-3.5" /><span className="hidden sm:inline">Kapcsolattartók</span></SegBtn>
          </div>
        )}
      </div>

      {/* Search Bar */}
      {activeTab !== 'chart' && (
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={activeTab === 'structure' ? 'Keresés egység, pozíció, név, e-mail vagy szoba szerint...' : 'Keresés név, beosztás, osztály, email vagy szoba szerint...'}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs transition-colors"
            />
          </div>
          {activeTab === 'directory' && (
            <div className="text-xs text-slate-500">
              Találatok: <span className="font-semibold text-slate-800 dark:text-slate-200">{filteredPersons.length}</span>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          1. TAB: SZERVEZET (egység → pozíció → munkatárs)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'structure' && (
        loading ? (
          <div className="py-16 text-center text-slate-500 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
            <span>Szervezet betöltése folyamatban...</span>
          </div>
        ) : (
          <OrgStructurePanel
            model={model}
            mode={structMode}
            search={search}
            canEdit={canEdit}
            h={{
              onAddUnit: (pid) => handleOpenNewUnit(pid),
              onEditUnit: (u) => handleOpenEditUnit(u),
              onDeleteUnit: (u) => handleDeleteUnit(u.id, u.name),
              onAddPosition: (uid) => handleOpenNewPos(uid),
              onEditPosition: (p) => { const raw = findPosition(p.id); if (raw) handleOpenEditPos(raw); },
              onDeletePosition: (p) => handleDeletePos(p.id, p.title),
              onAddPerson: (uid, pid) => handleOpenNewPerson(uid, pid),
              onEditPerson: (p) => { const raw = findPerson(p.id); if (raw) handleOpenEditPerson(raw); },
              onDeletePerson: (p) => handleDeletePerson(p.id, p.name),
            }}
          />
        )
      )}

      {/* ─────────────────────────────────────────────────────────────
          2. TAB: SZERVEZETI DIAGRAM (szerkesztő)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'chart' && (
        <div className="space-y-4">
          <div className="p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-end gap-3">
            <div>
              <span className={labelCls}>Tájolás</span>
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Tájolás">
                <SegBtn active={chartDraft.orientation === 'vertical'} onClick={() => setChartDraft({ ...chartDraft, orientation: 'vertical' })} title="Álló: az alegységek a fölérendelt alatt"><RectangleVertical className="w-3.5 h-3.5" /> Álló</SegBtn>
                <SegBtn active={chartDraft.orientation === 'horizontal'} onClick={() => setChartDraft({ ...chartDraft, orientation: 'horizontal' })} title="Fekvő: az alegységek a fölérendelttől jobbra"><RectangleHorizontal className="w-3.5 h-3.5" /> Fekvő</SegBtn>
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="oc-detail">Részletesség</label>
              <select id="oc-detail" value={chartDraft.detail} onChange={(e) => setChartDraft({ ...chartDraft, detail: e.target.value as OrgChartSettings['detail'] })}
                className="px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="units">Csak egységek</option>
                <option value="leaders">Egységek + vezetők</option>
                <option value="all">Egységek + minden munkatárs</option>
              </select>
            </div>
            <div className="flex-1 min-w-[12rem]">
              <label className={labelCls} htmlFor="oc-root">Gyökérelem felirata</label>
              <input id="oc-root" value={chartDraft.root_title} onChange={(e) => setChartDraft({ ...chartDraft, root_title: e.target.value })}
                placeholder={tenantName || 'A szervezet vezetése'} maxLength={200}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            {canEdit && (
              <button onClick={saveChartSettings} disabled={!chartDirty || chartSaving}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                {chartSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Beállítás mentése
              </button>
            )}
            {chartMsg && <span className="text-xs text-emerald-700 dark:text-emerald-400" role="status">{chartMsg}</span>}
          </div>

          {canEdit && (
            <div className="p-3 rounded-2xl border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/60 dark:bg-indigo-950/30 text-xs text-indigo-950 dark:text-indigo-200 flex items-start gap-2.5">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600 dark:text-indigo-400" />
              <div className="space-y-1">
                <p><b>Szerkesztés:</b> húzd az egységet egy másik egységre → alá kerül; a gyökérre húzva legfelső szintű lesz. Az egység fölé állva:
                  {' '}◀ ▶ (fekvőben ▲ ▼) sorrend, <b>Mellé / Alá</b> – törzskari (a vezető mellé kötött) vagy beosztott egység, <b>+</b> alegység, ceruza: szerkesztés.</p>
                <p>A tájolás, a részletesség és a felirat a <b>Beállítás mentése</b> után a nyilvános oldalon is érvényes; az átrendezés azonnal mentődik.</p>
              </div>
            </div>
          )}

          <div className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/40 relative ${chartBusy ? 'opacity-70' : ''}`}>
            {chartBusy && <RefreshCw className="w-4 h-4 animate-spin text-indigo-500 absolute top-3 right-3" />}
            {loading ? (
              <div className="py-16 text-center text-slate-500"><RefreshCw className="w-6 h-6 animate-spin text-indigo-500 inline" /></div>
            ) : (
              <OrgChart model={model} settings={chartDraft} rootTitle={chartDraft.root_title || tenantName} edit={chartHandlers} />
            )}
          </div>

          <div className="p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200">
              <Network className="w-4 h-4 text-indigo-600" /> Beszúrás a bejegyzésekbe (élő hivatkozás)
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              A bejegyzés leírásánál a <b>Változó</b> gomb → <i>Szervezet &amp; személyzet</i> csoportból választható, vagy másold be az alábbi hivatkozást.
              A nyilvános oldalon mindig az aktuális szervezeti adat jelenik meg.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[...tokenList, ...treeOrder.flatMap(({ u }) => [
                { token: scoped('SZERVEZETI_DIAGRAM', u.id), label: `Diagram – ${u.name}` },
                { token: scoped('KAPCSOLATOK', u.id), label: `Kapcsolattartók – ${u.name}` },
              ])].map((t) => (
                <button key={t.token} type="button" onClick={() => copyToken(t.token)} title={`${t.token} másolása`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] text-slate-700 dark:text-slate-300 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 cursor-pointer">
                  {copiedToken === t.token ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-slate-400" />}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          3. TAB: NÉVSOR (KÁRTYA & TÁBLÁZAT MÓD)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'directory' && dirMode !== 'contacts' && (
        loading ? (
          <div className="py-16 text-center text-slate-500 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
            <span>Munkatársak betöltése folyamatban...</span>
          </div>
        ) : dirMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedPersons.length === 0 ? (
              <div className="col-span-full py-12 text-center text-slate-500 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl">
                Nincs a keresésnek megfelelő munkatárs.
              </div>
            ) : (
              sortedPersons.map((p) => (
                <div
                  key={p.id}
                  className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs transition flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white">{p.name}</h3>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold mt-0.5">
                          {p.title || p.position_title || 'Munkatárs'}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                        p.active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30' 
                          : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
                      }`}>
                        {p.active ? 'Aktív' : 'Inaktív'}
                      </span>
                    </div>

                    <div className="mt-4 space-y-2 text-xs text-slate-600 dark:text-slate-400">
                      {p.org_unit_name && (
                        <div className="flex items-center gap-2">
                          <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate font-medium text-slate-700 dark:text-slate-300">{p.org_unit_name}</span>
                        </div>
                      )}
                      {p.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <a href={`mailto:${p.email}`} className="truncate hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                            {p.email}
                          </a>
                        </div>
                      )}
                      {p.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{p.phone}</span>
                        </div>
                      )}
                      {p.room && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>Iroda: {p.room}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {canEdit && (
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenEditPerson(p)}
                        className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
                        title="Módosítás"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePerson(p.id, p.name)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition cursor-pointer"
                        title="Törlés"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          /* Table View */
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th
                      onClick={() => handlePersonSort('name')}
                      className="px-5 py-3.5 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                      title="Rendezés név szerint"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span className={personSortCol === 'name' ? 'text-indigo-600 dark:text-cyan-400 font-bold' : ''}>Név & Beosztás</span>
                        {personSortCol === 'name' ? (
                          personSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                        )}
                      </div>
                    </th>

                    <th
                      onClick={() => handlePersonSort('org_unit')}
                      className="px-5 py-3.5 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                      title="Rendezés szervezeti egység szerint"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span className={personSortCol === 'org_unit' ? 'text-indigo-600 dark:text-cyan-400 font-bold' : ''}>Szervezeti Egység</span>
                        {personSortCol === 'org_unit' ? (
                          personSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                        )}
                      </div>
                    </th>

                    <th
                      onClick={() => handlePersonSort('contact')}
                      className="px-5 py-3.5 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                      title="Rendezés elérhetőség szerint"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span className={personSortCol === 'contact' ? 'text-indigo-600 dark:text-cyan-400 font-bold' : ''}>Elérhetőségek</span>
                        {personSortCol === 'contact' ? (
                          personSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                        )}
                      </div>
                    </th>

                    <th
                      onClick={() => handlePersonSort('room')}
                      className="px-5 py-3.5 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                      title="Rendezés szobaszám szerint"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span className={personSortCol === 'room' ? 'text-indigo-600 dark:text-cyan-400 font-bold' : ''}>Szoba</span>
                        {personSortCol === 'room' ? (
                          personSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                        )}
                      </div>
                    </th>

                    <th
                      onClick={() => handlePersonSort('status')}
                      className="px-5 py-3.5 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                      title="Rendezés státusz szerint"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span className={personSortCol === 'status' ? 'text-indigo-600 dark:text-cyan-400 font-bold' : ''}>Státusz</span>
                        {personSortCol === 'status' ? (
                          personSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                        )}
                      </div>
                    </th>

                    {canEdit && <th className="px-5 py-3.5 text-right">Műveletek</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {sortedPersons.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition">
                      <td className="px-5 py-3.5">
                        <div className="font-bold text-slate-900 dark:text-white">{p.name}</div>
                        <div className="text-xs text-indigo-600 dark:text-indigo-400">{p.title || p.position_title || '—'}</div>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                        {p.org_unit_name || '—'}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-600 dark:text-slate-400 space-y-0.5">
                        {p.email && <div>{p.email}</div>}
                        {p.phone && <div className="text-slate-500">{p.phone}</div>}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-600 dark:text-slate-400">
                        {p.room || '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                          p.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                          {p.active ? 'Aktív' : 'Inaktív'}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-5 py-3.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => handleOpenEditPerson(p)}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                              title="Módosítás"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePerson(p.id, p.name)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 transition cursor-pointer"
                              title="Törlés"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ─────────────────────────────────────────────────────────────
          3/b. KAPCSOLATTARTÓK
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'directory' && dirMode === 'contacts' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPersons.map((p) => (
              <div
                key={p.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">{p.name}</h3>
                      <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{p.title || 'Munkatárs'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{p.org_unit_name || 'Központi állomány'}</p>
                    </div>
                    <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                      <PhoneCall className="w-4 h-4" />
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2 text-xs">
                    {p.phone && (
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        <a href={`tel:${p.phone}`} className="font-semibold hover:text-indigo-600">{p.phone}</a>
                      </div>
                    )}
                    {p.email && (
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <a href={`mailto:${p.email}`} className="hover:text-indigo-600 truncate">{p.email}</a>
                      </div>
                    )}
                    {p.room && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        <span>Iroda: {p.room}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>Ügyfélfogadás: Munkanapokon hivatali időben</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: MUNKATÁRS HOZZÁADÁSA / MÓDOSÍTÁSA
      ───────────────────────────────────────────────────────────── */}
      {personModalOpen && editingPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col" role="dialog" aria-modal="true" aria-labelledby="person-modal-title">
            <h2 id="person-modal-title" className="text-lg font-bold text-slate-900 dark:text-white mb-4 shrink-0">
              {editingPerson.id ? 'Munkatárs Módosítása' : 'Új Munkatárs Hozzáadása'}
            </h2>
            <form onSubmit={handleSavePerson} className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div>
                <label className={labelCls} htmlFor="pm-name">Teljes Név *</label>
                <input id="pm-name" type="text" required autoFocus
                  value={editingPerson.name || ''}
                  onChange={(e) => setEditingPerson({ ...editingPerson, name: e.target.value })}
                  placeholder="pl. Dr. Minta János"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="pm-unit">Szervezeti Egység</label>
                  <select id="pm-unit"
                    value={editingPerson.org_unit_id || ''}
                    onChange={(e) => setEditingPerson({ ...editingPerson, org_unit_id: e.target.value ? Number(e.target.value) : null })}
                    className={inputCls}
                  >
                    <option value="">-- Nincs hozzárendelve --</option>
                    {unitOptions()}
                  </select>
                </div>

                <div>
                  <label className={labelCls} htmlFor="pm-pos">Pozíció / Tisztség</label>
                  <select id="pm-pos"
                    value={editingPerson.position_id || ''}
                    onChange={(e) => {
                      const posId = e.target.value ? Number(e.target.value) : null;
                      const matchedPos = positions.find(pos => pos.id === posId);
                      setEditingPerson(prev => {
                        if (!prev) return null;
                        const next = { ...prev, position_id: posId };
                        if (matchedPos?.org_unit_id && !next.org_unit_id) {
                          next.org_unit_id = matchedPos.org_unit_id;
                        }
                        if (matchedPos && !next.title) {
                          next.title = matchedPos.title;
                        }
                        return next;
                      });
                    }}
                    className={inputCls}
                  >
                    <option value="">-- Egyedi / Nem választott --</option>
                    {treeOrder.map(({ u }) => {
                      const ps = positions.filter((p) => p.org_unit_id === u.id);
                      return ps.length ? (
                        <optgroup key={u.id} label={u.name}>
                          {ps.map((pos) => <option key={pos.id} value={pos.id}>{pos.title}{pos.is_leader ? ' (vezető)' : ''}</option>)}
                        </optgroup>
                      ) : null;
                    })}
                    {positions.some((p) => !p.org_unit_id || !orgUnits.some((u) => u.id === p.org_unit_id)) && (
                      <optgroup label="Egységhez nem rendelt">
                        {positions.filter((p) => !p.org_unit_id || !orgUnits.some((u) => u.id === p.org_unit_id)).map((pos) => <option key={pos.id} value={pos.id}>{pos.title}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="pm-title">Egyedi Megnevezés / Beosztás</label>
                <input id="pm-title" type="text"
                  value={editingPerson.title || ''}
                  onChange={(e) => setEditingPerson({ ...editingPerson, title: e.target.value })}
                  placeholder="pl. Osztályvezető, Igazgatási Főelőadó"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="pm-email">E-mail Cím</label>
                  <input id="pm-email" type="email"
                    value={editingPerson.email || ''}
                    onChange={(e) => setEditingPerson({ ...editingPerson, email: e.target.value })}
                    placeholder="email@onkormanyzat.hu"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="pm-phone">Telefonszám</label>
                  <PhoneInput key={editingPerson.id ?? 'new'} id="pm-phone"
                    value={editingPerson.phone}
                    onChange={(v) => setEditingPerson({ ...editingPerson, phone: v })}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="pm-room">Iroda / Szobaszám</label>
                <input id="pm-room" type="text"
                  value={editingPerson.room || ''}
                  onChange={(e) => setEditingPerson({ ...editingPerson, room: e.target.value })}
                  placeholder="pl. I. emelet 102."
                  className={inputCls}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button type="button" onClick={() => setPersonModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 cursor-pointer">
                  Mégse
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition cursor-pointer shadow-sm">
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: SZERVEZETI EGYSÉG HOZZÁADÁSA / MÓDOSÍTÁSA
      ───────────────────────────────────────────────────────────── */}
      {unitModalOpen && editingUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col" role="dialog" aria-modal="true" aria-labelledby="unit-modal-title">
            <h2 id="unit-modal-title" className="text-lg font-bold text-slate-900 dark:text-white mb-4 shrink-0">
              {editingUnit.id ? 'Szervezeti Egység Módosítása' : 'Új Szervezeti Egység'}
            </h2>
            <form onSubmit={handleSaveUnit} className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div>
                <label className={labelCls} htmlFor="um-name">Egység Neve *</label>
                <input id="um-name" type="text" required autoFocus
                  value={editingUnit.name || ''}
                  onChange={(e) => setEditingUnit({ ...editingUnit, name: e.target.value })}
                  placeholder="pl. Pénzügyi és Gazdasági Osztály"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <div>
                  <label className={labelCls} htmlFor="um-parent">Fölérendelt egység</label>
                  <select id="um-parent" disabled={!hierarchyReady}
                    value={editingUnit.parent_id || ''}
                    onChange={(e) => setEditingUnit({ ...editingUnit, parent_id: e.target.value ? Number(e.target.value) : null })}
                    className={`${inputCls} disabled:opacity-50`}
                  >
                    <option value="">— A szervezet vezetése (legfelső szint) —</option>
                    {unitOptions(editingUnit.id ? descendantIds(model, editingUnit.id) : undefined)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="um-sort">Sorrend</label>
                  <input id="um-sort" type="number" min={0}
                    value={editingUnit.sort_order ?? 0}
                    onChange={(e) => setEditingUnit({ ...editingUnit, sort_order: Number(e.target.value) })}
                    className={`${inputCls} sm:w-24`}
                  />
                </div>
              </div>

              <fieldset disabled={!hierarchyReady} className="disabled:opacity-50">
                <legend className={labelCls}>Elhelyezés a diagramon</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {([
                    ['below', 'Alá (beosztott egység)', 'A fölérendelt egység alatt, hierarchiában alábontva.'],
                    ['beside', 'Mellé (törzskari egység)', 'A fölérendelt egység mellé kötve, oldalágon (pl. titkárság, belső ellenőrzés).'],
                  ] as const).map(([val, title, desc]) => (
                    <label key={val} className={`p-2.5 rounded-xl border cursor-pointer text-xs ${editingUnit.placement === val ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30' : 'border-slate-200 dark:border-slate-700'}`}>
                      <span className="flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-200">
                        <input type="radio" name="um-placement" value={val} checked={(editingUnit.placement || 'below') === val}
                          onChange={() => setEditingUnit({ ...editingUnit, placement: val })} className="text-indigo-600" />
                        {title}
                      </span>
                      <span className="block mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{desc}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset disabled={!hierarchyReady} className="space-y-3 disabled:opacity-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls} htmlFor="um-code">Azonosító / kód</label>
                    <input id="um-code" type="text"
                      value={editingUnit.code || ''}
                      onChange={(e) => setEditingUnit({ ...editingUnit, code: e.target.value })}
                      placeholder="pl. PGO"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="um-phone">Telefonszám</label>
                    <PhoneInput key={editingUnit.id ?? 'new'} id="um-phone" compact
                      value={editingUnit.phone}
                      onChange={(v) => setEditingUnit({ ...editingUnit, phone: v })}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls} htmlFor="um-email">E-mail</label>
                    <input id="um-email" type="email"
                      value={editingUnit.email || ''}
                      onChange={(e) => setEditingUnit({ ...editingUnit, email: e.target.value })}
                      placeholder="osztaly@onkormanyzat.hu"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="um-address">Cím / épület</label>
                    <input id="um-address" type="text"
                      value={editingUnit.address || ''}
                      onChange={(e) => setEditingUnit({ ...editingUnit, address: e.target.value })}
                      placeholder="pl. Fő tér 1., I. emelet"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="um-desc">Feladatkör / leírás</label>
                  <textarea id="um-desc" rows={2}
                    value={editingUnit.description || ''}
                    onChange={(e) => setEditingUnit({ ...editingUnit, description: e.target.value })}
                    placeholder="Az egység feladatainak rövid leírása..."
                    className={inputCls}
                  />
                </div>
              </fieldset>

              <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                Az egység vezetője a „vezetői tisztség” jelölésű pozícióból és annak betöltőjéből adódik – a pozíciót és a munkatársat az egység sorában veheted fel.
              </p>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button type="button" onClick={() => setUnitModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 cursor-pointer">
                  Mégse
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition cursor-pointer shadow-sm">
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: POZÍCIÓ HOZZÁADÁSA / MÓDOSÍTÁSA
      ───────────────────────────────────────────────────────────── */}
      {posModalOpen && editingPos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col" role="dialog" aria-modal="true" aria-labelledby="pos-modal-title">
            <h2 id="pos-modal-title" className="text-lg font-bold text-slate-900 dark:text-white mb-4 shrink-0">
              {editingPos.id ? 'Pozíció Módosítása' : 'Új Pozíció'}
            </h2>
            <form onSubmit={handleSavePos} className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div>
                <label className={labelCls} htmlFor="pos-title">Pozíció / Tisztség Megnevezése *</label>
                <input id="pos-title" type="text" required autoFocus
                  value={editingPos.title || ''}
                  onChange={(e) => setEditingPos({ ...editingPos, title: e.target.value })}
                  placeholder="pl. Főépítész, Igazgatási Előadó"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <div>
                  <label className={labelCls} htmlFor="pos-unit">Szervezeti Egység</label>
                  <select id="pos-unit"
                    value={editingPos.org_unit_id || ''}
                    onChange={(e) => setEditingPos({ ...editingPos, org_unit_id: e.target.value ? Number(e.target.value) : null })}
                    className={inputCls}
                  >
                    <option value="">-- Nincs hozzárendelve --</option>
                    {unitOptions()}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="pos-sort">Sorrend</label>
                  <input id="pos-sort" type="number" min={0}
                    value={editingPos.sort_order ?? 0}
                    onChange={(e) => setEditingPos({ ...editingPos, sort_order: Number(e.target.value) })}
                    className={`${inputCls} sm:w-24`}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1">
                <label className={`flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer ${!hierarchyReady ? 'opacity-50' : ''}`}>
                  <input type="checkbox" id="is_leader" disabled={!hierarchyReady}
                    checked={editingPos.is_leader === 1}
                    onChange={(e) => setEditingPos({ ...editingPos, is_leader: e.target.checked ? 1 : 0 })}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Vezetői tisztségnek minősül (az egység vezetője)
                </label>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox"
                    checked={!!editingPos.multi_person}
                    onChange={(e) => setEditingPos({ ...editingPos, multi_person: e.target.checked ? 1 : 0 })}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Több munkatárs is betöltheti
                </label>
              </div>

              <div>
                <label className={labelCls} htmlFor="pos-desc">Feladatkör / Leírás</label>
                <textarea id="pos-desc" rows={3} disabled={!hierarchyReady}
                  value={editingPos.job_description || ''}
                  onChange={(e) => setEditingPos({ ...editingPos, job_description: e.target.value })}
                  placeholder="A feladatkör rövid jogszabályi leírása..."
                  className={`${inputCls} disabled:opacity-50`}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button type="button" onClick={() => setPosModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 cursor-pointer">
                  Mégse
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition cursor-pointer shadow-sm">
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
